import React from 'react';
import { Trash2, Copy, Check, Link as LinkIcon, User } from 'lucide-react';
import { useTheme } from '../../ThemeContext';


export default function SeatsTable({
  seatsWithNames,
  invites,
  loadingSeats,
  userRole,
  authors,
  onRoleChange,
  onRemoveSeat,
  onLinkAuthor,
  onCopyInvite,
  onRevokeInvite,
  copiedInvite
}) {
  const { t, isDark } = useTheme();
  const getRoleDisplay = (role) => {
    const roles = {
      'primary_owner': 'Primary Owner',
      'owner': 'Owner',
      'admin': 'Admin',
      'guest': 'Guest'
    };
    return roles[role] || role;
  };

  const formatExpiresIn = (expiresAt) => {
    const now = Date.now();
    const diff = expiresAt - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} day${days !== 1 ? 's' : ''}`;
    if (hours > 0) return `${hours} hour${hours !== 1 ? 's' : ''}`;
    return 'Less than 1 hour';
  };

  const getLinkedAuthor = (authorId) => {
    if (!authorId || !authors) return null;
    return authors.find(a => a.id === authorId);
  };

  if (loadingSeats) {
    return <p style={{ textAlign: 'center', color: t.textSecondary, padding: '20px' }}>Loading members...</p>;
  }

  return (
    <div style={{ 
      border: '1px solid #e7e5e4',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: t.surface,
      marginBottom: '24px'
    }}>
      {/* Table Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1.5fr 1.2fr 1fr 0.5fr',
        gap: '16px',
        padding: '12px 20px',
        backgroundColor: t.modalBg,
        borderBottom: '1px solid #e7e5e4',
        fontSize: '11px',
        fontWeight: '600',
        color: t.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontFamily: 'Poppins, sans-serif'
      }}>
        <div>Member</div>
        <div>Linked Author</div>
        <div>Role</div>
        <div style={{ textAlign: 'right' }}>Status</div>
        <div></div>
      </div>

      {/* Seated Members */}
      {seatsWithNames.map((seat, index) => {
        const canChangeRole = userRole === 'primary_owner' && seat.role !== 'primary_owner';
        const canRemove = seat.role !== 'primary_owner' && (userRole === 'primary_owner' || userRole === 'owner');
        const linkedAuthor = getLinkedAuthor(seat.author_id);

        return (
          <div
            key={seat.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1.5fr 1.2fr 1fr 0.5fr',
              gap: '16px',
              padding: '16px 20px',
              borderBottom: (index < seatsWithNames.length - 1 || invites.length > 0) ? '1px solid #f5f5f4' : 'none',
              alignItems: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fefcf5'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            {/* Member Column */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: t.buttonBg,
                color: t.buttonText,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '13px',
                fontWeight: '600',
                fontFamily: 'Poppins, sans-serif',
                flexShrink: 0
              }}>
                {seat.display_name ? seat.display_name[0].toUpperCase() : seat.wallet_address.substring(0, 2).toUpperCase()}
              </div>
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: t.text,
                  fontFamily: 'Poppins, sans-serif',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  marginBottom: '2px'
                }}>
                  {seat.display_name}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: t.textSecondary,
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {seat.wallet_address.substring(0, 8)}...{seat.wallet_address.substring(seat.wallet_address.length - 6)}
                </div>
              </div>
            </div>

            {/* Linked Author Column */}
            <div>
              {linkedAuthor ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: t.borderStrong,
                    color: t.text,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: '600',
                    flexShrink: 0
                  }}>
                    {linkedAuthor.name[0].toUpperCase()}
                  </div>
                  <span style={{
                    fontSize: '13px',
                    color: t.text,
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: '500'
                  }}>
                    {linkedAuthor.name}
                  </span>
                </div>
              ) : (
                <button
                  onClick={() => onLinkAuthor && onLinkAuthor(seat.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 10px',
                    backgroundColor: 'transparent',
                    border: `1px dashed ${t.borderStrong}`,
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: t.textSecondary,
                    fontFamily: 'Poppins, sans-serif',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = t.accent;
                    e.currentTarget.style.color = t.text;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = t.borderStrong;
                    e.currentTarget.style.color = '#78716c';
                  }}
                >
                  <User size={14} />
                  Link Author
                </button>
              )}
            </div>

            {/* Role Column */}
            <div>
              {canChangeRole ? (
                <select
                  value={seat.role}
                  onChange={(e) => onRoleChange(seat.id, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    border: `1px solid ${t.borderStrong}`,
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: t.text,
                    fontFamily: 'Poppins, sans-serif',
                    backgroundColor: t.surface,
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = t.accent}
                  onBlur={(e) => e.target.style.borderColor = t.borderStrong}
                >
                  <option value="owner">Owner</option>
                  <option value="admin">Admin</option>
                  <option value="guest">Guest</option>
                </select>
              ) : (
                <div style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: `1px solid ${t.borderStrong}`,
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: t.text,
                  fontFamily: 'Poppins, sans-serif',
                  backgroundColor: '#f5f5f4',
                  cursor: 'not-allowed',
                  opacity: 0.8
                }}>
                  {getRoleDisplay(seat.role)}
                </div>
              )}
            </div>

            {/* Status Column */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                fontWeight: '500',
                color: t.success,
                fontFamily: 'Poppins, sans-serif'
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#15803d'
                }} />
                Active
              </div>
            </div>

            {/* Actions Column */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {canRemove && (
                <button
                  onClick={() => onRemoveSeat(seat.id, seat.role)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: t.textSecondary,
                    padding: '6px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fee2e2';
                    e.currentTarget.style.color = t.error;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#78716c';
                  }}
                  title="Remove member"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Pending Invites */}
      {invites.map((invite, index) => (
        <div
          key={invite.id}
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1.5fr 1.2fr 1fr 0.5fr',
            gap: '16px',
            padding: '16px 20px',
            borderBottom: index < invites.length - 1 ? '1px solid #f5f5f4' : 'none',
            alignItems: 'center',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fefcf5'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          {/* Member Column - Invite Link */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: t.borderStrong,
              color: t.textSecondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '13px',
              fontWeight: '600',
              fontFamily: 'Poppins, sans-serif',
              flexShrink: 0
            }}>
              <LinkIcon size={16} />
            </div>
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: t.textSecondary,
                fontFamily: 'Poppins, sans-serif',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                marginBottom: '2px'
              }}>
                Invite Link
              </div>
              <div style={{
                fontSize: '12px',
                color: t.textSecondary,
                fontFamily: 'Poppins, sans-serif',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {invite.uses_count}/{invite.max_uses} used · Expires in {formatExpiresIn(invite.expires_at)}
              </div>
            </div>
          </div>

          {/* Linked Author Column - Empty for invites */}
          <div style={{ color: t.textSecondary, fontSize: '12px', fontStyle: 'italic' }}>
            Set on accept
          </div>

          {/* Role Column */}
          <div>
            <div style={{
              width: '100%',
              padding: '6px 10px',
              border: `1px solid ${t.borderStrong}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '500',
              color: t.text,
              fontFamily: 'Poppins, sans-serif',
              backgroundColor: '#f5f5f4',
              cursor: 'not-allowed',
              opacity: 0.8
            }}>
              {getRoleDisplay(invite.role)}
            </div>
          </div>

          {/* Status Column - Pending */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              fontWeight: '500',
              color: '#ea580c',
              fontFamily: 'Poppins, sans-serif'
            }}>
              <div style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#ea580c'
              }} />
              Pending
            </div>
          </div>

          {/* Actions Column */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
            <button
              onClick={() => onCopyInvite(invite.invite_code)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: t.textSecondary,
                padding: '6px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f4';
                e.currentTarget.style.color = t.text;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#78716c';
              }}
              title="Copy invite link"
            >
              {copiedInvite === invite.invite_code ? <Check size={16} /> : <Copy size={16} />}
            </button>
            <button
              onClick={() => onRevokeInvite(invite.id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: t.textSecondary,
                padding: '6px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fee2e2';
                e.currentTarget.style.color = t.error;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#78716c';
              }}
              title="Revoke invite"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
